import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FullEditDealDialog = ({
  open,
  onOpenChange,
  deal,
  getAuthHeaders,
  onSaveSuccess,
  // Called AFTER status was flipped to Default via the mark-default
  // endpoint (split-submit path). Parent opens DefaultStatusWorkflowDialog.
  onStatusChangedToDefault
}) => {
  const [formData, setFormData] = useState({});
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('core');

  // Load contacts for dropdowns
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await axios.get(`${API}/contacts`, { headers: getAuthHeaders() });
        setContacts(res.data || []);
      } catch (err) {
        console.error('Failed to fetch contacts:', err);
      }
    };
    if (open) fetchContacts();
  }, [open, getAuthHeaders]);

  // Initialize form data when deal changes
  useEffect(() => {
    if (deal) {
      setFormData({
        // Core Deal Info
        deal_id: deal.deal_id || '',
        deal_shortcode: deal.deal_shortcode || '',
        deal_name: deal.deal_name || '',
        status: deal.status || 'Funded',
        position: deal.position || '',
        
        // Contacts
        company_id: deal.company_id || '',
        broker_id: deal.broker_id || '',
        funder_id: deal.funder_id || '',
        cofunded_partner_id: deal.cofunded_partner_id || '',   // 10/15: co-funded partner (pays the broker); '' = house deal
        // BL-028j Phase 2 (d): originator classification editable via this dialog
        originator_label: deal.originator_label || 'unset',
        
        // Financial Terms
        funding_date: deal.funding_date?.split('T')[0] || '',
        funding_amount: deal.funding_amount || '',
        rate: deal.rate || '',
        term_days: deal.term_days || '',
        payback: deal.payback || '',
        payment_amount: deal.payment_amount || '',
        payment_frequency: deal.payment_frequency || (deal.weekly ? 'Weekly' : 'Daily'),
        estimated_end_date: deal.estimated_end_date?.split('T')[0] || '',
        
        // Fees & Commissions
        fees: deal.fees || '',
        refi_balance_payoff: deal.refi_balance_payoff || '',
        funder_percentage: deal.funder_percentage || '',
        commission_rate: deal.commission_rate || '',
        broker_split_rate: deal.broker_split_rate || '',
        // origination_fee field removed May 2026 (consolidated to `fees`).
        collection_fee_rate: deal.collection_fee_rate || '',
        // BL-026: commission_paid removed — derived from commission_rate × funding_amount.
        distribute_adjustments: deal.distribute_adjustments === true,
        is_iso_originated: deal.is_iso_originated === true,
        
        // Business Info (deal-specific snapshot)
        legal_business_name: deal.legal_business_name || '',
        business_address: deal.business_address || '',
        ein_tax_id: deal.ein_tax_id || '',
        monthly_revenue: deal.monthly_revenue || '',
        
        // Owner Info (deal-specific snapshot - SSN/bank now on Contact)
        owner_name: deal.owner_name || '',
        owner_phone: deal.owner_phone || '',
        owner_email: deal.owner_email || '',
        owner_dob: deal.owner_dob?.split('T')[0] || '',
        owner_address: deal.owner_address || '',
        
        // Notes
        notes: deal.notes || ''
      });
    }
  }, [deal]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Bidirectional Funding × Rate ↔ Payback recompute.
      // Whichever value the user just typed is the "source of truth"; the other
      // dependent value is derived. Rate uses 6dp so payback round-trips exactly.
      const toNum = (v) => {
        if (v === '' || v === null || v === undefined) return NaN;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : NaN;
      };
      const trimRate = (n) => n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
      const funding = toNum(next.funding_amount);
      const rate = toNum(next.rate);
      const payback = toNum(next.payback);

      if (field === 'funding_amount') {
        // Prefer the existing rate to recompute payback (rate is the more deliberate input)
        if (Number.isFinite(funding) && Number.isFinite(rate) && rate > 0) {
          next.payback = +(funding * rate).toFixed(2);
        } else if (Number.isFinite(funding) && Number.isFinite(payback) && funding > 0) {
          next.rate = trimRate(payback / funding);
        }
      } else if (field === 'rate') {
        if (Number.isFinite(funding) && Number.isFinite(rate) && funding > 0) {
          next.payback = +(funding * rate).toFixed(2);
        }
      } else if (field === 'payback') {
        if (Number.isFinite(funding) && Number.isFinite(payback) && funding > 0) {
          next.rate = trimRate(payback / funding);
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Fields that should default to 0 if empty (not null)
      const numericFieldsWithDefault = [
        'funding_amount', 'rate', 'term_days', 'payback', 'payment_amount',
        'fees', 'refi_balance_payoff', 'funder_percentage'
      ];
      
      // Fields that can be null
      const numericFieldsNullable = [
        'commission_rate', 'broker_split_rate', 'collection_fee_rate', 'monthly_revenue',
      ];

      const cleanedData = { ...formData };
      
      // Handle fields with default values - use 0 instead of null
      numericFieldsWithDefault.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
          cleanedData[field] = 0;
        } else {
          cleanedData[field] = parseFloat(cleanedData[field]) || 0;
        }
      });
      
      // Handle nullable numeric fields - remove if empty to avoid overwriting
      numericFieldsNullable.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
          delete cleanedData[field];
        } else {
          cleanedData[field] = parseFloat(cleanedData[field]) || null;
        }
      });

      // Convert empty date strings to null, or remove if empty
      const dateFields = ['funding_date', 'estimated_end_date', 'owner_dob'];
      dateFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
          delete cleanedData[field];
        }
      });
      
      // Handle weekly flag based on payment_frequency (backend uses both)
      if (cleanedData.payment_frequency) {
        cleanedData.weekly = cleanedData.payment_frequency === 'Weekly';
      }
      
      // Convert empty string IDs to null
      ['company_id', 'broker_id', 'funder_id'].forEach(field => {
        if (!cleanedData[field] || cleanedData[field] === 'none') cleanedData[field] = null;
      });

      // ─── Default-flip branch (May 2026) ────────────────────────────
      // If the user is flipping status to "Default" for the first time,
      // route the status change through the mark-default endpoint so the
      // backend captures the default_snapshot, inserts the 25% DEFAULT_FEE
      // penalty, stamps is_defaulted / defaulted_at, and recalcs balance.
      // Other field edits in the SAME submit go through a follow-up PATCH
      // afterward. Any other status transition (and any non-status edit)
      // takes the unchanged PATCH path so we don't regress those flows.
      const isFlippingToDefault = (
        cleanedData.status === 'Default' &&
        deal.status !== 'Default'
      );

      if (isFlippingToDefault) {
        const { status, ...otherFieldsRaw } = cleanedData;

        // JSON-stringify equality handles null/undefined and numeric edge
        // cases without false positives from strict reference equality.
        // `?? null` normalizes undefined → null on both sides so a missing
        // key on `deal` matches a deleted key on `cleanedData`.
        const hasOtherChanges = Object.keys(otherFieldsRaw).some((k) => {
          if (k === 'updated_at' || k === 'tenant') return false;
          return JSON.stringify(otherFieldsRaw[k] ?? null)
              !== JSON.stringify(deal[k] ?? null);
        });

        // STEP 1: mark-default. Any failure aborts before touching other
        // fields. The outer try/catch will toast.
        try {
          await axios.post(
            `${API}/deals/${deal.id}/mark-default`,
            {},
            { headers: getAuthHeaders() }
          );
        } catch (markErr) {
          const detail = markErr.response?.data?.detail;
          const msg = typeof detail === 'string'
            ? detail
            : detail?.message || 'Failed to mark deal as Default';
          throw new Error(msg);
        }

        // STEP 2: deal is now Default + consistent. Apply non-status edits
        // if any. PATCH failure here is non-fatal — the status flip already
        // succeeded; the workflow should still open. Toast describes both
        // outcomes.
        let otherEditsError = null;
        if (hasOtherChanges) {
          try {
            await axios.patch(
              `${API}/deals/${deal.id}`,
              otherFieldsRaw,
              { headers: getAuthHeaders() }
            );
          } catch (patchErr) {
            const detail = patchErr.response?.data?.detail;
            otherEditsError = typeof detail === 'string'
              ? detail
              : detail?.message || patchErr.message || 'Unknown error';
          }
        }

        if (otherEditsError) {
          toast.error(
            `Deal marked Default successfully. Other edits failed: ${otherEditsError}. Re-submit your other changes.`,
            { duration: 8000 }
          );
        }

        onOpenChange(false);
        if (onStatusChangedToDefault) onStatusChangedToDefault();
        if (onSaveSuccess) onSaveSuccess();
      } else {
        // EXISTING PATH — unchanged. Covers every other status transition
        // and every non-status-change edit.
        await axios.patch(`${API}/deals/${deal.id}`, cleanedData, {
          headers: getAuthHeaders()
        });

        onOpenChange(false);
        if (onSaveSuccess) onSaveSuccess();
      }
    } catch (error) {
      console.error('Save error:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  if (!deal) return null;

  const merchants = contacts.filter(c => c.is_merchant);
  const brokers = contacts.filter(c => c.is_broker || c.is_iso);
  const funders = contacts.filter(c => c.is_funder);
  const investors = contacts.filter(c => c.is_investor);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Deal #{deal?.deal_id} - Full Details</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-5 mb-4">
            <TabsTrigger value="core">Core Info</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="business">Business</TabsTrigger>
            <TabsTrigger value="owner">Owner</TabsTrigger>
            <TabsTrigger value="other">Other</TabsTrigger>
          </TabsList>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {/* Core Info Tab */}
            <TabsContent value="core" className="space-y-4 mt-0">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deal_id">Deal ID #</Label>
                  <Input
                    id="deal_id"
                    value={formData.deal_id || ''}
                    onChange={(e) => handleFieldChange('deal_id', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deal_shortcode">Shortcode</Label>
                  <Input
                    id="deal_shortcode"
                    value={formData.deal_shortcode || ''}
                    onChange={(e) => handleFieldChange('deal_shortcode', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Select
                    value={formData.position || ''}
                    onValueChange={(value) => handleFieldChange('position', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1st Position</SelectItem>
                      <SelectItem value="2">2nd Position</SelectItem>
                      <SelectItem value="3">3rd Position</SelectItem>
                      <SelectItem value="4">4th Position</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deal_name">Deal Name</Label>
                <Input
                  id="deal_name"
                  value={formData.deal_name || ''}
                  onChange={(e) => handleFieldChange('deal_name', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status || 'Funded'}
                  onValueChange={(value) => handleFieldChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Funded">Funded</SelectItem>
                    <SelectItem value="Payment Issues">Payment Issues</SelectItem>
                    <SelectItem value="In-House Collection">In-House Collection</SelectItem>
                    <SelectItem value="Default">Default</SelectItem>
                    <SelectItem value="Collected">Collected</SelectItem>
                    <SelectItem value="Paid in Full">Paid in Full</SelectItem>
                    <SelectItem value="Paid in Full (Refinanced)">Paid in Full (Refinanced)</SelectItem>
                    <SelectItem value="Paid in Full / Settled">Paid in Full / Settled</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Merchant</Label>
                  <Select
                    value={formData.company_id || 'none'}
                    onValueChange={(value) => handleFieldChange('company_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select merchant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {merchants.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.company_name} {m.shortcode ? `(${m.shortcode})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Broker/ISO</Label>
                  <Select
                    value={formData.broker_id || 'none'}
                    onValueChange={(value) => handleFieldChange('broker_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select broker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {brokers.map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Funder</Label>
                  <Select
                    value={formData.funder_id || 'none'}
                    onValueChange={(value) => handleFieldChange('funder_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select funder" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {funders.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Co-funded by (partner who pays the broker)</Label>
                  <Select
                    value={formData.cofunded_partner_id || 'none'}
                    onValueChange={(value) => handleFieldChange('cofunded_partner_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger data-testid="cofunded-partner-select">
                      <SelectValue placeholder="House deal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- House deal (PFS / DIB pays the broker) --</SelectItem>
                      {investors.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">On a co-funded deal every PFS / DIB position owes the partner its % of the broker commission.</p>
                </div>
                {/* BL-028j Phase 2 (d): Originator classification */}
                <div className="space-y-2">
                  <Label htmlFor="originator_label">Originator</Label>
                  <Select
                    value={formData.originator_label || 'unset'}
                    onValueChange={(value) => handleFieldChange('originator_label', value)}
                  >
                    <SelectTrigger data-testid="edit-deal-originator">
                      <SelectValue placeholder="Select originator" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pfs">PFS</SelectItem>
                      <SelectItem value="dib">DIB</SelectItem>
                      <SelectItem value="enod">Enod</SelectItem>
                      <SelectItem value="river">River</SelectItem>
                      <SelectItem value="parkside">Parkside</SelectItem>
                      <SelectItem value="bluetie">Bluetie</SelectItem>
                      <SelectItem value="milvado">Milvado</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="unset">Unset (review later)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Financial Tab */}
            <TabsContent value="financial" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="funding_date">Funding Date</Label>
                  <Input
                    id="funding_date"
                    type="date"
                    value={formData.funding_date || ''}
                    onChange={(e) => handleFieldChange('funding_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimated_end_date">Estimated End Date</Label>
                  <Input
                    id="estimated_end_date"
                    type="date"
                    value={formData.estimated_end_date || ''}
                    onChange={(e) => handleFieldChange('estimated_end_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="funding_amount">Funding Amount</Label>
                  <Input
                    id="funding_amount"
                    type="number"
                    step="0.01"
                    value={formData.funding_amount || ''}
                    onChange={(e) => handleFieldChange('funding_amount', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate">Rate (Factor)</Label>
                  <Input
                    id="rate"
                    type="number"
                    step="0.01"
                    value={formData.rate || ''}
                    onChange={(e) => handleFieldChange('rate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="term_days">Term (Days)</Label>
                  <Input
                    id="term_days"
                    type="number"
                    value={formData.term_days || ''}
                    onChange={(e) => handleFieldChange('term_days', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payback">Payback Amount</Label>
                  <Input
                    id="payback"
                    type="number"
                    step="0.01"
                    value={formData.payback || ''}
                    onChange={(e) => handleFieldChange('payback', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_amount">Payment Amount</Label>
                  <Input
                    id="payment_amount"
                    type="number"
                    step="0.01"
                    value={formData.payment_amount || ''}
                    onChange={(e) => handleFieldChange('payment_amount', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_frequency">Payment Frequency</Label>
                  <Select
                    value={formData.payment_frequency || 'Daily'}
                    onValueChange={(value) => handleFieldChange('payment_frequency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Daily">Daily</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Bi-Weekly">Bi-Weekly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fees">Fees</Label>
                  <Input
                    id="fees"
                    type="number"
                    step="0.01"
                    value={formData.fees || ''}
                    onChange={(e) => handleFieldChange('fees', e.target.value)}
                  />
                </div>
                {/* Origination Fee input removed May 2026 — consolidated to `fees`. */}
                <div className="space-y-2">
                  <Label htmlFor="refi_balance_payoff">Refi Balance Payoff</Label>
                  <Input
                    id="refi_balance_payoff"
                    type="number"
                    step="0.01"
                    value={formData.refi_balance_payoff || ''}
                    onChange={(e) => handleFieldChange('refi_balance_payoff', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="funder_percentage">Funder Percentage (%)</Label>
                  <Input
                    id="funder_percentage"
                    type="number"
                    step="0.01"
                    value={formData.funder_percentage || ''}
                    onChange={(e) => handleFieldChange('funder_percentage', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="commission_rate">Commission Rate (%)</Label>
                  <Input
                    id="commission_rate"
                    type="number"
                    step="0.01"
                    value={formData.commission_rate || ''}
                    onChange={(e) => handleFieldChange('commission_rate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="broker_split_rate">Broker Split (%)</Label>
                  <Input
                    id="broker_split_rate"
                    type="number"
                    step="0.01"
                    value={formData.broker_split_rate || ''}
                    onChange={(e) => handleFieldChange('broker_split_rate', e.target.value)}
                  />
                </div>
              </div>

              {/* BL-026: commission_paid field removed — derived from
                  commission_rate × funding_amount via utils/deal_summary.commission_amount. */}

              {/* Distribution Engine Settings */}
              <div className="border-t pt-4 mt-2">
                <p className="text-sm font-medium text-gray-700 mb-3">Distribution Engine</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="collection_fee_rate">Collection Fee Rate (%)</Label>
                    <Input
                      id="collection_fee_rate"
                      data-testid="collection-fee-rate-input"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="e.g. 10 for 10%"
                      value={formData.collection_fee_rate || ''}
                      onChange={(e) => handleFieldChange('collection_fee_rate', e.target.value)}
                    />
                    <p className="text-xs text-gray-500">Charged to PARTNER and INVESTOR_FEE syndicators per payment</p>
                  </div>
                  <div className="space-y-4">
                    <Label className="block">Distribute Adjustments</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="checkbox"
                        id="distribute_adjustments"
                        data-testid="distribute-adjustments-toggle"
                        checked={formData.distribute_adjustments === true}
                        onChange={(e) => handleFieldChange('distribute_adjustments', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor="distribute_adjustments" className="text-sm text-gray-700 cursor-pointer">
                        Also auto-distribute NSF, reversals &amp; adjustments
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">When enabled, ACH returns, refunds, and adjustments also trigger syndicator distributions</p>
                    <div className="flex items-center gap-3 mt-2">
                      <input
                        type="checkbox"
                        id="is_iso_originated"
                        data-testid="is-iso-originated-toggle"
                        checked={formData.is_iso_originated === true}
                        onChange={(e) => handleFieldChange('is_iso_originated', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor="is_iso_originated" className="text-sm text-gray-700 cursor-pointer">
                        ISO-Originated Deal
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">Adds a 2% underwriting charge for non-PARTNER syndicators</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Business Info Tab */}
            <TabsContent value="business" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="legal_business_name">Legal Business Name</Label>
                <Input
                  id="legal_business_name"
                  value={formData.legal_business_name || ''}
                  onChange={(e) => handleFieldChange('legal_business_name', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="business_address">Business Address</Label>
                <Textarea
                  id="business_address"
                  value={formData.business_address || ''}
                  onChange={(e) => handleFieldChange('business_address', e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ein_tax_id">EIN / Tax ID</Label>
                  <Input
                    id="ein_tax_id"
                    value={formData.ein_tax_id || ''}
                    onChange={(e) => handleFieldChange('ein_tax_id', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="monthly_revenue">Monthly Revenue</Label>
                  <Input
                    id="monthly_revenue"
                    type="number"
                    step="0.01"
                    value={formData.monthly_revenue || ''}
                    onChange={(e) => handleFieldChange('monthly_revenue', e.target.value)}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Owner Info Tab */}
            <TabsContent value="owner" className="space-y-4 mt-0">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> SSN and bank account information is now managed on the Contact profile for security. 
                  This section only contains deal-specific owner snapshots.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="owner_name">Owner Name</Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name || ''}
                    onChange={(e) => handleFieldChange('owner_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_dob">Owner DOB</Label>
                  <Input
                    id="owner_dob"
                    type="date"
                    value={formData.owner_dob || ''}
                    onChange={(e) => handleFieldChange('owner_dob', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="owner_phone">Owner Phone</Label>
                <Input
                  id="owner_phone"
                  value={formData.owner_phone || ''}
                  onChange={(e) => handleFieldChange('owner_phone', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="owner_email">Owner Email</Label>
                <Input
                  id="owner_email"
                  type="email"
                  value={formData.owner_email || ''}
                  onChange={(e) => handleFieldChange('owner_email', e.target.value)}
                  placeholder="owner@example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="owner_address">Owner Address</Label>
                <Textarea
                  id="owner_address"
                  value={formData.owner_address || ''}
                  onChange={(e) => handleFieldChange('owner_address', e.target.value)}
                  rows={2}
                />
              </div>
            </TabsContent>

            {/* Other Tab */}
            <TabsContent value="other" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ''}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  rows={6}
                  placeholder="Additional notes about this deal..."
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
        
        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FullEditDealDialog;
